import React from 'react';
import { Link } from 'react-router-dom';
import styled from 'styled-components';

const FooterContainer = styled.footer`
  background: ${props => props.theme.colors.backgroundDark};
  color: white;
  padding: 3rem 0 2rem;
  margin-top: auto;
`;

const FooterContent = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 2rem;
  
  @media (max-width: 768px) {
    padding: 0 1rem;
  }
`;

const FooterGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: 2rem;
  margin-bottom: 2rem;
  
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 2rem;
  }
`;

const FooterSection = styled.div`
  h3 {
    font-family: ${props => props.theme.fonts.script};
    font-size: 1.5rem;
    margin-bottom: 1rem;
    color: ${props => props.theme.colors.primary};
  }
  
  h4 {
    font-size: 1.2rem;
    margin-bottom: 1rem;
    color: white;
  }
  
  p {
    margin-bottom: 0.5rem;
    color: rgba(255, 255, 255, 0.8);
    line-height: 1.6;
  }
  
  ul {
    list-style: none;
    padding: 0;
    margin: 0;
  }
  
  li {
    margin-bottom: 0.5rem;
  }
  
  a {
    color: rgba(255, 255, 255, 0.8);
    text-decoration: none;
    transition: color 0.3s ease;
    
    &:hover {
      color: ${props => props.theme.colors.primary};
    }
  }
`;

const SocialLinks = styled.div`
  display: flex;
  gap: 1rem;
  margin-top: 1rem;
  
  a {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 40px;
    height: 40px;
    background: rgba(255, 255, 255, 0.1);
    border-radius: 50%;
    font-size: 1.2rem;
    transition: all 0.3s ease;
    
    &:hover {
      background: ${props => props.theme.colors.primary};
      transform: translateY(-2px);
    }
  }
`;

const FooterBottom = styled.div`
  border-top: 1px solid rgba(255, 255, 255, 0.1);
  padding-top: 2rem;
  text-align: center;
  
  p {
    margin-bottom: 0.5rem;
    color: rgba(255, 255, 255, 0.6);
    font-size: 0.9rem;
  }
  
  a {
    color: rgba(255, 255, 255, 0.6);
    text-decoration: none;
    
    &:hover {
      color: ${props => props.theme.colors.primary};
    }
  }
  
  @media (max-width: 768px) {
    text-align: left;
  }
`;

const CustomerPortalButton = styled.a`
  display: inline-block;
  margin-top: 1rem;
  padding: 0.6rem 1rem;
  background: ${props => props.theme.colors.primary};
  color: #ffffff !important;
  border-radius: 6px;
  font-weight: 600;
  text-decoration: none !important;
  transition: all 0.2s ease;
  
  &:hover {
    opacity: 0.9;
    transform: translateY(-1px);
  }
`;

const ContactInfo = styled.div`
  p {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    margin-bottom: 0.75rem;
    
    span:first-child {
      font-size: 1.1rem;
    }
  }
`;

const FooterLogo = styled.img`
  height: 3rem;
  width: auto;
  object-fit: contain;
  margin-bottom: 1rem;
  filter: drop-shadow(0 2px 4px rgba(0, 0, 0, 0.2));
  
  @media (max-width: 768px) {
    height: 2.5rem;
  }
`;

const Footer = () => {
  return (
    <FooterContainer>
      <FooterContent>
        <FooterGrid>
          <FooterSection>
            <FooterLogo src="/assets/logo.webp" alt="Der Ponystall Logo" />
            <h3>Der Ponystall</h3>
            <p>Marcella Deeken</p>
            <p>Tiergestützte Pädagogik & Förderung</p>
            <p style={{ marginTop: '1rem', lineHeight: '1.6' }}>
              Entdecken Sie die wunderbare Welt der tiergestützten Pädagogik. 
              Unsere Ponys sind therapeutische Partner für Menschen jeden Alters.
            </p>
          </FooterSection>

          <FooterSection>
            <h4>Navigation</h4>
            <ul>
              <li><Link to="/">Startseite</Link></li>
              <li><Link to="/marcella">Marcella Deeken</Link></li>
              <li><Link to="/kurse">Kurse</Link></li>
              <li><Link to="/team">Team</Link></li>
              <li><Link to="/ponys">Ponys</Link></li>
            </ul>
          </FooterSection>

          <FooterSection>
            <h4>Angebote</h4>
            <ul>
              <li><Link to="/kurse/ponyclub">Ponyclub</Link></li>
              <li><Link to="/kurse/muki-club">MuKi-Club</Link></li>
              <li><Link to="/kurse/heilpaedagogisches-reiten">Heilpädagogisches Reiten</Link></li>
              <li><Link to="/institutionen">Institutionen</Link></li>
            </ul>
          </FooterSection>

          <FooterSection>
            <h4>Kontakt</h4>
            <ContactInfo>
              <p>
                <span>📍</span>
                <span>Weidenweg 2, 26683 Saterland</span>
              </p>
              <p>
                <span>📞</span>
                <a href={`tel:${process.env.REACT_APP_CONTACT_PHONE || '01621353794'}`}>
                  {process.env.REACT_APP_CONTACT_PHONE || '0162 1353794'}
                </a>
              </p>
              <p>
                <span>✉️</span>
                <a href={`mailto:${process.env.REACT_APP_CONTACT_EMAIL || 'derponystall@gmx.de'}`}>
                  {process.env.REACT_APP_CONTACT_EMAIL || 'derponystall@gmx.de'}
                </a>
              </p>
            </ContactInfo>
            
            <SocialLinks>
              <a href="#" aria-label="Facebook" title="Facebook">
                📘
              </a>
              <a href="#" aria-label="Instagram" title="Instagram">
                📷
              </a>
            </SocialLinks>
          </FooterSection>
        </FooterGrid>

        <FooterBottom>
          <p>&copy; 2024 Der Ponystall - Marcella Deeken. Alle Rechte vorbehalten.</p>
          <p>
            <Link to="/datenschutz">Datenschutz</Link> | {' '}
            <Link to="/impressum">Impressum</Link> | {' '}
            <Link to="/agb">AGB</Link>
          </p>
          <CustomerPortalButton as={Link} to="/kundenportal">Kundenportal</CustomerPortalButton>
        </FooterBottom>
      </FooterContent>
    </FooterContainer>
  );
};

export default Footer;